import * as Yup from 'yup';
import {
  ArrayFormFieldsRecord,
  FormFieldsRecord,
} from '../../../../helpers/formHelpers/types';
import { startOfDay } from 'date-fns';
import { createObjectFromKey } from '../../../../helpers/formHelpers';
import { FetchStampCardResponse } from '../../../../services/StampCards/useFetchMerchantConnectStampCard';
import { useTenantContext } from '../../../../contexts/TenantContext';
import { ProductDetails } from '../../../../services/StampCards/types';
import {
  mcEndDateValidation,
  mcStartDateValidation,
} from '../../../../helpers/formHelpers/validationConstants';

type FormBuilder = {
  productDetailsFormFields: FormFieldsRecord;
  productDetailsInitialValues: ProductDetails;
  productDetailsFormSchema: {};
  loyaltyRewardableSettingsFormFields: ArrayFormFieldsRecord;
  loyaltyRewardableSettingsInitialValues: Pick<
    ProductDetails,
    'loyalty_rewardable_settings'
  >;
  loyaltyRewardableSettingsFormSchema: {};
  loyaltyRewardableSettingsBaseValues: {};
};

type StampCardFormProps = {
  selectedMaxStamps: number;
  isEditMode: boolean;
  loyaltyRewardableSettingsData: FetchStampCardResponse | undefined;
};

export const useMerchantConnectStampCardFormBuilder = ({
  selectedMaxStamps,
  isEditMode,
  loyaltyRewardableSettingsData,
}: StampCardFormProps): FormBuilder => {
  const { tenantCurrency } = useTenantContext();

  const requiredStampsCountOptions = Array.from({
    length: selectedMaxStamps,
  }).map((_, index) => ({
    key: `${index + 1}`,
    label: `${index + 1}`,
    value: `${index + 1}`,
  }));

  // uses basic form fields
  const productDetailsFormFields: FormFieldsRecord = {
    // 1 row 3 fields
    min_spending_amount: {
      label: 'FavePay Minimum Amount',
      type: 'number',
      key: 'min_spending_amount',
      col: 8,
      value:
        loyaltyRewardableSettingsData?.product_details.min_spending_amount
          .amount,
      precision: 2,
      addonBefore:
        loyaltyRewardableSettingsData?.product_details.min_spending_amount
          .currency || tenantCurrency,
      schema: Yup.number()
        .moreThan(0.01, 'Minimum is 0.01')
        .typeError('Required')
        .required('Required'),
      required: true,
    },
    total_stamps_count: {
      label: 'Maximum Stamps',
      type: 'select',
      key: 'total_stamps_count',
      options: [
        {
          key: '5',
          label: '5',
          value: '5',
        },
        {
          key: '10',
          label: '10',
          value: '10',
        },
      ],
      col: 8,
      value: loyaltyRewardableSettingsData?.product_details.total_stamps_count,
      schema: Yup.string().required('Required'),
      required: true,
    },
    duration: {
      label: 'Duration',
      type: 'number',
      key: 'duration',
      col: 8,
      value: loyaltyRewardableSettingsData?.product_details.duration,
      schema: Yup.number().min(0, 'Minimum is 0').nullable(),
    },
    // 1 row 2 fields
    start_date: {
      label: 'Start Date',
      type: 'datepicker',
      key: 'start_date',
      col: 12,
      value: loyaltyRewardableSettingsData?.product_details.start_date,
      disabledDate: currentDate =>
        // cannot select past dates
        currentDate < startOfDay(new Date()),
      schema: mcStartDateValidation('start_date'),
      required: true,
    },
    end_date: {
      label: 'End Date',
      type: 'datepicker',
      key: 'end_date',
      col: 12,
      value: loyaltyRewardableSettingsData?.product_details.end_date,
      disabledDate: currentDate =>
        // cannot select past dates
        currentDate < startOfDay(new Date()),
      schema: mcEndDateValidation('start_date'),
      required: true,
    },
    // 1 row 1 field
    description: {
      label: 'Description',
      type: 'textarea',
      key: 'description',
      value: loyaltyRewardableSettingsData?.product_details.description,
      schema: Yup.string().required('Required'),
      required: true,
    },
    // 1 row 1 field
    fine_print: {
      label: 'Fine Print',
      type: 'editor',
      key: 'fine_print',
      value: loyaltyRewardableSettingsData?.product_details.fine_print,
      schema: Yup.string().nullable(),
    },
  };

  const productDetailsInitialValues = createObjectFromKey(
    productDetailsFormFields,
    'value' as keyof FormFieldsRecord[keyof FormFieldsRecord],
  ) as unknown as ProductDetails;

  const productDetailsFormSchema = createObjectFromKey(
    productDetailsFormFields,
    'schema',
  );

  // uses array form fields
  const loyaltyRewardableSettingsFormFields: ArrayFormFieldsRecord = {
    // this field is hidden, but is passed to the API
    rewardable_type: {
      label: '',
      type: 'text',
      key: 'rewardable_type',
      col: 0,
      value: 'EagleActivity',
    },
    [`rewardable_details.name`]: {
      label: 'Deal Name',
      type: 'text',
      key: `rewardable_details.name`,
      col: 24,
      value: undefined,
      required: true,
    },
    [`rewardable_details.description`]: {
      label: 'Deal Description',
      type: 'text',
      key: `rewardable_details.description`,
      col: 24,
      value: undefined,
      required: true,
    },
    required_stamps_count: {
      label: 'Number of Stamps',
      type: 'select',
      key: 'required_stamps_count',
      col: 12,
      options: requiredStampsCountOptions,
      value: undefined,
      required: true,
    },
    ['rewardable_details.validity_days']: {
      label: 'Validity',
      type: 'number',
      key: 'rewardable_details.validity_days',
      col: 12,
      value: undefined,
      required: true,
    },
    ['rewardable_details.redemption_start_date']: {
      label: 'Redemption Start Date',
      type: 'datepicker',
      key: `rewardable_details.redemption_start_date`,
      col: 12,
      value: undefined,
      disabledDate: currentDate =>
        // cannot select past dates
        currentDate < startOfDay(new Date()),
      required: true,
    },
    ['rewardable_details.redemption_end_date']: {
      label: 'Redemption End Date',
      type: 'datepicker',
      key: 'rewardable_details.redemption_end_date',
      col: 12,
      value: undefined,
      disabledDate: currentDate =>
        // cannot select past dates
        currentDate < startOfDay(new Date()),
      required: true,
    },
    // 1 row 1 field
    ['rewardable_details.what_you_get']: {
      label: 'What You Get',
      type: 'editor',
      key: 'rewardable_details.what_you_get',
      value: undefined,
    },
    // 1 row 1 field
    ['rewardable_details.fine_print']: {
      label: 'Fine Print',
      type: 'editor',
      key: 'rewardable_details.fine_print',
      value: undefined,
    },
    ['rewardable_details.redemption_instructions']: {
      label: 'Redemption Instructions',
      type: 'editor',
      key: 'rewardable_details.redemption_instructions',
      value: undefined,
    },
    ['rewardable_details.images']: {
      label: 'Image (Eagle Activity)',
      type: 'image',
      key: 'rewardable_details.images',
      value: undefined,
      maxCount: 1,
    },
  };

  const loyaltyRewardableSettingsInitialValues = {
    loyalty_rewardable_settings: isEditMode
      ? loyaltyRewardableSettingsData?.product_details.loyalty_rewardable_settings.map(
          rewardableSetting => ({
            ...rewardableSetting,
            rewardable_details: {
              ...rewardableSetting.rewardable_details,
              redemption_start_date: new Date(
                rewardableSetting.rewardable_details.redemption_start_date,
              ),

              redemption_end_date: new Date(
                rewardableSetting.rewardable_details.redemption_end_date,
              ),
              images: rewardableSetting.rewardable_details.image_urls?.map(
                image => {
                  return {
                    url: image,
                    thumbUrl: image,
                    uid: image,
                    name: 'default-file',
                  };
                },
              ),
            },
          }),
        )
      : [
          {
            ...createObjectFromKey(
              loyaltyRewardableSettingsFormFields as FormFieldsRecord,
              'value' as keyof FormFieldsRecord[keyof FormFieldsRecord],
            ),
            rewardable_details: {
              validity_days: 30,
            },
          },
        ],
  } as Pick<ProductDetails, 'loyalty_rewardable_settings'>;

  const loyaltyRewardableSettingsFormSchema = {
    loyalty_rewardable_settings: Yup.array().of(
      Yup.object().shape({
        required_stamps_count: Yup.string()
          .required('Required')
          .test(
            'is-valid-stamp-count',
            'Number of stamps must be less than or equal to the total stamps count',
            value => {
              const totalStampsCount = selectedMaxStamps;
              return Number(value) <= Number(totalStampsCount);
            },
          ),
        rewardable_details: Yup.object().shape({
          name: Yup.string().required('Required'),
          description: Yup.string().required('Required'),
          redemption_start_date: mcStartDateValidation('redemption_start_date'),
          redemption_end_date: mcEndDateValidation('redemption_start_date'),
          validity_days: Yup.number()
            .integer('Must be an integer')
            .moreThan(0, 'Minimum is 1')
            .typeError('Required')
            .required('Required'),
          what_you_get: Yup.string().nullable(),
          fine_print: Yup.string().nullable(),
          redemption_instruction: Yup.string().nullable(),
          images: Yup.array().of(
            Yup.mixed().test(
              'fileSize',
              'The file size is too large. Maximum allowed size is 10MB.',
              value => {
                const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

                // in edit mode, value.size is not available
                if (!value.size) return true;
                // Ensure a file is provided and check the size
                return value.size <= MAX_FILE_SIZE;
              },
            ),
          ),
        }),
      }),
    ),
  };

  // used when user creates new rewardable settings
  const loyaltyRewardableSettingsBaseValues = {
    rewardable_type: 'EagleActivity',
    required_stamps_count: undefined,
    rewardable_details: {
      name: undefined,
      description: undefined,
      redemption_start_date: undefined,
      redemption_end_date: undefined,
      validity_days: 30,
      images: undefined,
      what_you_get: undefined,
      fine_print: undefined,
      redemption_instructions: undefined,
    },
  };

  return {
    productDetailsFormFields,
    productDetailsInitialValues: productDetailsInitialValues,
    productDetailsFormSchema,
    loyaltyRewardableSettingsFormFields,
    loyaltyRewardableSettingsInitialValues,
    loyaltyRewardableSettingsFormSchema,
    loyaltyRewardableSettingsBaseValues,
  };
};
